import { preloadQueryOnHover, preloadRouteQuery } from "app/utils/routePreload";

import UserDetailsRootQueryNode from "./__generated__/UserDetailsRootQuery.graphql";

import type {
  UserDetailsRootQuery,
  UserDetailsRootQuery$variables,
} from "./__generated__/UserDetailsRootQuery.graphql";
import type { PreloadedRoute } from "app/utils/routePreload";
import type { LoaderFunctionArgs } from "react-router";
import type { FetchPolicy, IEnvironment } from "relay-runtime";

// This module is the user details route's loader chunk: it must not import the
// page, or anything that reaches it, or the loader could not run until the
// page's chunk had downloaded — which is the wait it exists to remove. That is
// also why the query comes from __generated__ rather than from the page's
// `graphql` tag.

/**
 * How the user details query is fetched, by the loader and the page alike.
 *
 * Always over the network, even with the user already in the store: the
 * personal API key create and edit flows redirect back to this page, and a
 * store hit would render the user's keys as they were before the change.
 */
export const USER_DETAILS_FETCH_POLICY: FetchPolicy = "store-and-network";

/**
 * Builds the user details query's variables.
 *
 * Both the route loader and the page itself call this, so that the page
 * recognises a preloaded query as already fetching what it wants (see
 * `useRoutePreloadedQuery`) — the two must agree property for property.
 *
 * @param key - The user's key, as it appears in the path.
 */
export function userDetailsVariables(
  key: string | number,
): UserDetailsRootQuery$variables {
  return { key: Number(key) };
}

/**
 * Builds the user details route's loader: it starts the query as the route is
 * navigated to, rather than waiting for the page's chunk to download and
 * render.
 *
 * @param environment - Defaults to the app's Relay environment; tests pass a
 *   mock, which must be the same environment the page renders under.
 */
export function createUserDetailsLoader(
  environment?: IEnvironment,
): (
  args: LoaderFunctionArgs,
) => Promise<PreloadedRoute<UserDetailsRootQuery> | null> {
  return async ({ params }) =>
    preloadRouteQuery<UserDetailsRootQuery>(
      UserDetailsRootQueryNode,
      userDetailsVariables(params.key ?? ""),
      { environment, fetchPolicy: USER_DETAILS_FETCH_POLICY },
    );
}

/**
 * Starts the user details query for a user the viewer is hovering in a list,
 * with the variables the details page will ask for when the link is followed —
 * so nothing is wasted if they click, and the query is dropped if they don't.
 */
export function preloadUserDetailsOnHover(key: string | number): void {
  preloadQueryOnHover<UserDetailsRootQuery>(
    UserDetailsRootQueryNode,
    userDetailsVariables(key),
  );
}
